import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { ResourceTypeSchema, SubscriptionTypeSchema } from './integration.models.resource';

extendZodWithOpenApi(z);

export const ResourceSubscriptionMetadataSchema = z.object({}).passthrough();

/**
 * Base definition of `resource_subscriptions` table.
 */
export const ResourceSubscriptionBaseSchema = z.object({
  id: z.string().uuid().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  resource_id: z.string().uuid().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  account_id: z.string().uuid().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  managing_user_id: z
    .string()
    .uuid()
    .optional()
    .openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  connection_id: z
    .string()
    .uuid()
    .optional()
    .openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  enabled: z.boolean().openapi({ example: true }),
  supports_webhooks: z.boolean().openapi({ example: true }),
  created_at: z
    .string()
    .datetime({ offset: true })
    .openapi({ example: '2023-07-08T00:30:26.207264+00:00' }),
  synced_at: z
    .string()
    .datetime({ offset: true })
    .openapi({ example: '2023-07-08T00:30:26.207264+00:00' })
    .nullable(),
  metadata: ResourceSubscriptionMetadataSchema,
});

export const ResourceSubscriptionWriteSchema = ResourceSubscriptionBaseSchema.omit({
  id: true,
  created_at: true,
  synced_at: true,
});

/**
 * Models
 */
export const SubscribableResourceBaseSchema = ResourceSubscriptionBaseSchema.pick({
  resource_id: true,
  connection_id: true,
  synced_at: true,
  enabled: true,
}).extend({
  subscription_id: z.string().uuid().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  title: z.string().openapi({ example: 'My Subscription' }),
  is_root: z.boolean().openapi({ example: true }).default(false),
  icon_url: z.string().optional().nullable().openapi({ example: 'https://example.com/icon.png' }),
  webhook_supported: z.boolean().openapi({ example: true }),
  webhook_enabled: z.boolean().openapi({ example: true }),
  webhook_active: z.boolean().openapi({ example: true }),
  external_type: z.string().optional().openapi({ example: 'workspace' }),
  resource_type: ResourceTypeSchema.optional().openapi({
    example: 'project',
    description:
      'Profile-aware resource type (e.g., board_as_client maps Trello boards to client). Optional for legacy providers.',
  }),
  user_can_modify: z.boolean().optional().openapi({ example: true }),
  subscription_type: SubscriptionTypeSchema,
});

export type SubscribableResourceBase = z.infer<typeof SubscribableResourceBaseSchema>;

export const PartialBatchUpdateSubscriptionsSchema = SubscribableResourceBaseSchema.pick({
  subscription_id: true,
  resource_id: true,
  enabled: true,
}).required();

export type PartialBatchUpdateSubscriptions = z.infer<typeof PartialBatchUpdateSubscriptionsSchema>;

/**
 * Resources that can be subscribed to.
 * Root resources returned at top-level, with one level of direct children as `sub_resources`.
 */
export const SubscribableResourceSchema = SubscribableResourceBaseSchema.extend({
  sub_subscriptions_enabled_count: z.coerce.number().int().openapi({ example: 1 }).default(0),
  sub_subscriptions_total_count: z.coerce.number().int().openapi({ example: 1 }).default(0),
  sub_subscriptions: z.array(SubscribableResourceBaseSchema).nullable(),
});

export type SubscribableResource = z.infer<typeof SubscribableResourceSchema>;

export const CrupdateSubscriptionSchema = z.object({
  id: z.string().uuid().optional().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  resource_id: z.string().uuid().openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  connection_id: z
    .string()
    .uuid()
    .optional()
    .openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
  enabled: z.boolean().openapi({ example: true }),
  // Probably shouldn't let end-users control this?
  // webhook_enabled: z.boolean().optional().openapi({ example: true }),
  managing_user_id: z
    .string()
    .uuid()
    .optional()
    .openapi({ example: '1c5e0e34-ccff-42b4-956b-6591e19293d6' }),
});

export type CrupdateSubscriptionBody = z.infer<typeof CrupdateSubscriptionSchema>;
